<script lang="ts">
  import { onMount } from 'svelte';

  // Scoped CSS (all under .azimuth-live). Order mirrors the original app:
  // theme vars first, then transcript, then shell overrides, then scan panel.
  import '$lib/azimuth-live/styles/pi-theme.css';
  import '$lib/azimuth-live/styles/pi-transcript.css';
  import '$lib/azimuth-live/styles/styles.css';
  import '$lib/azimuth-live/styles/scan-panel.css';

  // The client (d3 + elkjs + marked + highlight.js) is heavy and browser-only,
  // so it's dynamically imported in onMount — off the critical path, never run
  // during prerender. The shell markup below renders immediately (prerendered)
  // and fills with live data once mounted.
  onMount(() => {
    // On very small phones the graph is too cramped to be useful; skip the
    // heavy live client (d3/elkjs/SSE) entirely there.
    if (window.matchMedia('(max-width: 360px)').matches) return;

    let teardown: (() => void) | undefined;
    let cancelled = false;
    import('$lib/azimuth-live/main')
      .then(({ mountAzimuthLive }) => {
        if (!cancelled) teardown = mountAzimuthLive();
      })
      .catch((err) => console.error('azimuth-live failed to mount:', err));
    return () => {
      cancelled = true;
      teardown?.();
    };
  });
</script>

<section class="azimuth-live" aria-label="Live Azimuth protocol scan">
  <!-- Hero statement above the live viz. -->
  <div class="hero-bar">
    <p class="hero-subtitle"><span class="hero-arrow" aria-hidden="true">↓</span> Watch it in action <span class="hero-arrow" aria-hidden="true">↓</span></p>
  </div>
  <div class="shell">
    <section class="pane graph-pane">
      <div class="graph-wrap">
        <div id="protocol-graph" class="protocol-graph"></div>
        <!-- Decorative red scan sweep; overlays the graph SVG (DOM order). -->
        <div class="graph-scan" aria-hidden="true"></div>
        <!-- Attack agent rendered as a node that docks onto the contract it's
             working on (positioned/tethered by scan-panel.ts). -->
        <div id="attack-ship" class="attack-ship" aria-hidden="true">
          <div class="attack-ship-head"><span class="attack-ship-dot"></span>Azimuth</div>
          <div id="attack-ship-msg" class="attack-ship-msg"></div>
        </div>
        <!-- Intentionally empty: the scan-panel client picks the correct stage
             (Constructing / Loading next protocol / …) and animates it in once
             scan/current resolves, so no placeholder text flashes on load. -->
        <div id="graph-overlay" class="graph-overlay"></div>
        <!-- Active protocol name + graph stats, overlaid bottom-right of the graph. -->
        <div id="scan-title" class="scan-title">Waiting for next protocol…</div>
        <div id="graph-meta" class="graph-meta"></div>
      </div>
    </section>
    <div class="left-col">
      <section class="pane chat-pane">
        <div class="pane-header"><h2>Researching potential targets...</h2><span class="live-tag"><span class="live-dot" aria-hidden="true"></span>Live</span></div>
        <div id="transcript" class="transcript pi-transcript"></div>
      </section>
    </div>
    <div class="right-col">
      <section class="pane activity-pane">
        <div class="pane-header"><h2>Azimuth Attack Agent</h2><span class="live-tag"><span class="live-dot" aria-hidden="true"></span>Live</span></div>
        <div id="activity-log" class="activity-log"></div>
      </section>
    </div>
    <!-- Idle-only indicator beside the centered investigation agent: signals
         that the system is waiting on the next scan. Fades out once one loads. -->
    <div class="scan-loading" aria-hidden="true">
      <span class="scan-loading-dots"><i></i><i></i><i></i></span>
      <span>Hunting for whales</span>
    </div>
  </div>
</section>
